import { ReactNode, useMemo } from 'react'
import styled from 'styled-components'
import { GridColumn, RowId, SortState } from '../../types/grid.types'
import { useGridSort } from '../../hooks/useGridSort'
import { useGridPagination } from '../../hooks/useGridPagination'
import { useCollapsibleSummary } from '../../hooks/useCollapsibleSummary'
import Panel from '../Panel/Panel'
import Toolbar from '../Toolbar/Toolbar'
import TableScroll from './TableScroll'
import GridHead from './GridHead'
import GridBody from './GridBody'
import GridFooter from '../Footer/GridFooter'

export interface GridTableProps<T> {
  columns: GridColumn<T>[]
  rows: T[]
  getRowId: (row: T) => RowId
  isLoading?: boolean
  onRowClick?: (row: T) => void

  /** Toolbar */
  title?: ReactNode
  count?: ReactNode
  toolbarActions?: ReactNode

  /** Summary strip between the toolbar and the column header (e.g. <TableSummary />). */
  summary?: ReactNode
  /** Summary scrolls away with the first rows and comes back only at the top (iOS large-title style). */
  collapsibleSummary?: boolean

  /** Sorting */
  initialSort?: SortState<T>

  /** Pagination */
  paginated?: boolean
  perPageOptions?: number[]
  initialPerPage?: number

  /** Empty state */
  emptyTitle?: string
  emptySubtitle?: string
  emptyIcon?: ReactNode
}

const DEFAULT_PER_PAGE_OPTIONS = [50, 100, 150]

export const GridTable = <T,>({
  columns,
  rows,
  getRowId,
  isLoading,
  onRowClick,
  title,
  count,
  toolbarActions,
  summary,
  collapsibleSummary = false,
  initialSort = null,
  paginated = true,
  perPageOptions = DEFAULT_PER_PAGE_OPTIONS,
  initialPerPage = 50,
  emptyTitle,
  emptySubtitle,
  emptyIcon,
}: GridTableProps<T>) => {
  const visibleColumns = useMemo(
    () => columns.filter((col) => col.visible !== false),
    [columns],
  )

  const { sortState, sortedRows, toggleSort } = useGridSort<T>({
    rows,
    columns: visibleColumns,
    initial: initialSort,
  })

  const pagination = useGridPagination<T>({
    rows: sortedRows,
    initialPerPage,
  })

  const bodyRows = paginated ? pagination.pageRows : sortedRows
  const showFooter = paginated && !isLoading
  const hasToolbar = title != null || count != null || toolbarActions != null
  const isCollapsible = collapsibleSummary && summary != null
  const { containerRef, scrollRef, summaryRef } = useCollapsibleSummary(isCollapsible)

  const tableEl = (
    <table>
      <GridHead columns={visibleColumns} sortState={sortState} onSort={toggleSort} />
      <GridBody
        columns={visibleColumns}
        rows={bodyRows}
        getRowId={getRowId}
        isLoading={isLoading}
        onRowClick={onRowClick}
        emptyTitle={emptyTitle}
        emptySubtitle={emptySubtitle}
        emptyIcon={emptyIcon}
      />
    </table>
  )

  const table = (
    <TableScroll ref={scrollRef}>
      {isCollapsible ? (
        <>
          <SummarySpacer aria-hidden />
          <ScrollFill>{tableEl}</ScrollFill>
        </>
      ) : (
        tableEl
      )}
    </TableScroll>
  )

  return (
    <Panel>
      {hasToolbar && (
        <Toolbar title={title} count={count ?? rows.length}>
          {toolbarActions}
        </Toolbar>
      )}

      {isCollapsible ? (
        <CollapsibleArea ref={containerRef}>
          <SummaryOverlay ref={summaryRef}>{summary}</SummaryOverlay>
          {table}
        </CollapsibleArea>
      ) : (
        <>
          {summary}
          {table}
        </>
      )}

      {showFooter && (
        <GridFooter
          rangeStart={pagination.rangeStart}
          rangeEnd={pagination.rangeEnd}
          total={pagination.total}
          page={pagination.page}
          pageCount={pagination.pageCount}
          perPage={pagination.perPage}
          perPageOptions={perPageOptions}
          onPage={pagination.setPage}
          onPerPage={pagination.setPerPage}
        />
      )}
    </Panel>
  )
}

export default GridTable

/**
 * Scroll area with the summary laid over its top edge. The `--summary-*` variables are written
 * by useCollapsibleSummary; the sticky header follows the summary's bottom edge.
 */
const CollapsibleArea = styled.div`
  --grid-head-top: calc(var(--summary-h, 0px) - var(--summary-offset, 0px));
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`

const SummaryOverlay = styled.div`
  position: absolute;
  inset-block-start: 0;
  inset-inline: 0;
  z-index: 3;
  background: ${({ theme }) => theme.colors.surface};
  /* Flattens from the top edge, so the bottom edge stays glued to the first row. */
  transform-origin: top;
  transform: translate3d(0, var(--summary-bounce, 0px), 0) scaleY(var(--summary-scale, 1));
  will-change: transform;
  /* Drags/wheel on the summary fall through to the scroll area, so it scrolls (and collapses) natively. */
  pointer-events: none;

  a,
  button,
  input,
  select,
  [role='button'] {
    pointer-events: auto;
  }
`

/** Reserves the overlay's height at the top of the scroll content. */
const SummarySpacer = styled.div`
  height: var(--summary-h, 0px);
`

/**
 * At least one viewport tall, so the scroll range always covers the summary height —
 * otherwise a short report could never scroll the summary fully away.
 */
const ScrollFill = styled.div`
  min-height: 100%;
`
